// Save/load: localStorage with multiple slots. Serializes economy state, terrain heights,
// paths, fences, amenities and animals.
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { capForTier } from './economy.js';
import { applyBuildingConfigSnapshot, getBuildingConfigSnapshot } from '../world/buildingConfig.js';

const SLOT_PREFIX = 'zootycoon_save_';
export const SLOT_COUNT = 3;
export const AUTOSAVE_SLOT = 3; // extra hidden slot, written automatically

// Helper-CPU: serialization worker (falls back to sync if workers unavailable)
let saveWorker = null;
try {
  saveWorker = new Worker(new URL('../core/save-worker.js', import.meta.url));
  saveWorker._pending = new Map();
  saveWorker.onmessage = (e) => {
    const cb = saveWorker._pending.get(e.data.id);
    saveWorker._pending.delete(e.data.id);
    cb?.(e.data.json);
  };
} catch { saveWorker = null; }
let saveSeq = 0;

function heightsToB64(f32) {
  const bytes = new Uint8Array(f32.buffer, f32.byteOffset, f32.byteLength);
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(bin);
}
function b64ToHeights(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Float32Array(bytes.buffer);
}

export function listSlots() {
  return Array.from({ length: SLOT_COUNT + 1 }, (_, i) => {
    try {
      const raw = localStorage.getItem(SLOT_PREFIX + i);
      if (!raw) return null;
      const meta = JSON.parse(raw).meta;
      return meta;
    } catch { return null; }
  });
}

export function saveGame(world, slot, silent = false) {
  const s = store.get();
  const data = {
    meta: {
      zooName: s.zooName, mapSize: s.mapSize,
      day: s.day, month: s.month, year: s.year,
      money: s.money, savedAt: Date.now(),
    },
    state: {
      zooName: s.zooName, mapSize: s.mapSize, money: s.money, loan: s.loan, loanInterest: s.loanInterest,
      reputation: s.reputation, prestige: s.prestige, day: s.day, month: s.month, year: s.year, season: s.season,
      entranceFee: s.entranceFee, staff_keeper: s.staff_keeper, staff_cleaner: s.staff_cleaner, staff_guide: s.staff_guide, graphics: s.graphics,
      stars: s.stars, collected: s.collected, trophies: s.trophies, perks: s.perks, missionProgress: s.missionProgress,
      catStaff: s.catStaff, catTier: s.catTier,
      projects: s.projects,
      animalCap: s.animalCap, permitTier: s.permitTier,
    },
    terrain: { seed: world.terrain.seed, heights: null, waterLevel: world.terrain.waterLevel },
    buildingConfig: getBuildingConfigSnapshot(),
    paths: [...world.build.paths],
    fences: [...world.build.fences.keys()],
    amenities: world.amenities.items.map((i) => ({
      type: i.type,
      cx: i.cx,
      cz: i.cz,
      facing: i.facing == null ? null : i.facing,
      tier: i.tier == null ? null : i.tier,
      staff: i.staff == null ? null : i.staff,
      closed: !!i.closed,
      name: i.name || '',
    })),
    props: world.props ? world.props.items.map((i) => ({ type: i.type, cx: i.cx, cz: i.cz })) : [],
    animals: world.animals.animals.map((a) => {
      let x = 0, z = 0;
      if (a.model) { x = a.model.position.x; z = a.model.position.z; }
      else if (a.spawnCell) { x = (a.spawnCell.cx + 0.5) * world.terrain.cellSize - world.terrain.worldSize / 2; z = (a.spawnCell.cz + 0.5) * world.terrain.cellSize - world.terrain.worldSize / 2; }
      return {
        species: a.species, sex: a.sex, enclosureId: a.enclosureId, maturity: a.maturity, name: a.name, ageDays: a.ageDays,
        wellbeing: a.wellbeing, pregnantDays: a.pregnantDays, morph: a.morph || null, x, z,
      };
    }),
    staff: world.economy.staff,
  };
  const finish = (json) => {
    try {
      localStorage.setItem(SLOT_PREFIX + slot, json);
      if (!silent) bus.emit('ui:toast', { text: `Game saved to slot ${slot + 1}.`, kind: 'info' });
    } catch (e) {
      if (!silent) bus.emit('ui:toast', { text: 'Save failed — storage full?', kind: 'error' });
    }
  };
  if (saveWorker) {
    // helper CPU: copy heights and hand off serialization; zero main-thread hitch
    const copy = world.terrain.heights.slice();
    const id = ++saveSeq;
    saveWorker._pending.set(id, finish);
    saveWorker.postMessage({ id, heights: copy.buffer, data }, [copy.buffer]);
  } else {
    data.terrain.heights = heightsToB64(world.terrain.heights);
    finish(JSON.stringify(data));
  }
}

export function loadSlotData(slot) {
  const raw = localStorage.getItem(SLOT_PREFIX + slot);
  return raw ? JSON.parse(raw) : null;
}

// Restores world state after the world objects have been created from the save's terrain.
export function applySave(world, data) {
  applyBuildingConfigSnapshot(data.buildingConfig, true);
  store.set({ ...data.state, screen: 'game', speed: 1, activeTool: null, openPanel: null, zooClosed: false });
  // Migrate the animal cap onto the dynamic species-scaled tiers (old saves stored a static cap).
  store.set({ animalCap: capForTier(data.state.permitTier) });

  // Restore mission progress (mission system was constructed before this ran)
  if (world.missions) {
    const mp = data.state.missionProgress;
    if (mp) { world.missions.index = mp.index || 0; world.missions._base = mp.base || null; }
    world.missions._publish();
  }

  data.paths.forEach((key) => world.build.paths.add(key));
  data.fences.forEach((key) => world.build.fences.set(key, true));
  world.build._rebuildPaths();
  world.build._rebuildFences();
  world.build._detectEnclosures();

  // Keep trees out of restored PATHS (building footprints are cleared by amenities.restore below).
  if (world.vegetation && world.vegetation.userData.clearCells) {
    world.vegetation.userData.clearCells(new Set(data.paths), world.terrain.cellSize, world.terrain.worldSize / 2);
  }

  data.amenities.forEach(({ type, cx, cz, facing, tier, staff, closed, name }) => world.amenities.restore(type, cx, cz, { facing, tier, staff, closed, name }));
  (data.props || []).forEach(({ type, cx, cz }) => world.props.restore(type, cx, cz));

  const t = world.terrain;
  const half = t.worldSize / 2;
  data.animals.forEach((a) => {
    const cx = Math.max(0, Math.min(t.cells - 1, Math.floor((a.x + half) / t.cellSize)));
    const cz = Math.max(0, Math.min(t.cells - 1, Math.floor((a.z + half) / t.cellSize)));
    const enc = world.build.enclosures.find((e) => e.cells.has(`${cx},${cz}`)) || world.build.enclosures[0];
    if (!enc) return;
    const animal = world.animals.spawn(a.species, enc, cx, cz, a.maturity, a.morph || null);
    animal.sex = a.sex;
    if (a.name) animal.name = a.name;
    animal.ageDays = a.ageDays || 0;
    animal.wellbeing = a.wellbeing;
    animal.pregnantDays = a.pregnantDays;
  });
  world.animals._reflowRender(); // collapse young into per-species+sex badges after sexes are set

  world.economy.staff = { ...data.staff };
  // Sync the store's per-role counts from the restored economy staff so the UI (Finance expenses,
  // Tour Guides, etc.) matches — the saved state block used to omit staff_guide, resetting it to 0.
  const _st = world.economy.staff || {};
  store.set({ staff_keeper: _st.keeper || 0, staff_cleaner: _st.cleaner || 0, staff_guide: _st.guide || 0 });
  if (!store.get().graphics) store.set({ graphics: { auto: true, quality: 2, treeDensity: 0.3, fpsCap: 30, shadowRes: 128, muted: false } });
  bus.emit('gfx:apply');
  pushNews(`Welcome back to ${data.state.zooName}!`, 'success');
}

export { b64ToHeights };
